import { Card, CardContent } from '../components/ui/card';
import { Headphones, Play, ExternalLink, Clock } from 'lucide-react';

const podcasts = [
  {
    id: '1',
    title: 'Portland Tech Stories',
    description: 'Weekly interviews with Portland tech founders, investors, and community leaders sharing their journeys and insights.',
    host: 'Portland Tech Network',
    episodes: 127,
    image_url: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400',
    website: 'https://example.com/podcast1',
  },
  {
    id: '2',
    title: 'PDX Startup Radio',
    description: 'Deep dives into Portland\'s startup ecosystem, featuring funding rounds, exits, and emerging trends.',
    host: 'Startup Portland',
    episodes: 84,
    image_url: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=400',
    website: 'https://example.com/podcast2',
  },
  {
    id: '3',
    title: 'Tech & Trees',
    description: 'Exploring the intersection of technology and sustainability in the Pacific Northwest.',
    host: 'Green Tech PDX',
    episodes: 52,
    image_url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400',
    website: 'https://example.com/podcast3',
  },
  {
    id: '4',
    title: 'The Portland Engineer',
    description: 'Technical deep dives with Portland\'s top software engineers on architecture, tools, and best practices.',
    host: 'PDX Dev Community',
    episodes: 96,
    image_url: 'https://images.unsplash.com/photo-1593697821028-7cc59cfd7399?w=400',
    website: 'https://example.com/podcast4',
  },
];

const videoContent = [
  {
    id: '1',
    title: 'Portland Tech Week 2025 Highlights',
    description: 'Recap of the biggest tech event in Portland featuring keynotes, panels, and networking moments.',
    duration: '24:30',
    thumbnail: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400',
  },
  {
    id: '2',
    title: 'Building AI Startups in Portland',
    description: 'Panel discussion on the growing AI ecosystem and opportunities in Portland.',
    duration: '45:12',
    thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400',
  },
  {
    id: '3',
    title: 'Coworking Spaces Tour: PDX Edition',
    description: 'Virtual tour of Portland\'s best coworking spaces for tech professionals and startups.',
    duration: '18:45',
    thumbnail: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400',
  },
];

export const PodcastsPage = () => {
  return (
    <div data-testid="podcasts-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Media</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Podcasts & Media</h1>
          <p className="text-muted-foreground max-w-2xl">
            Listen to local tech podcasts, watch video content, and stay connected 
            with Portland's technology community through audio and video.
          </p>
        </div>
      </section>

      {/* Podcasts */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <h2 className="text-heading text-2xl mb-8">Featured Podcasts</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {podcasts.map((podcast, index) => (
              <Card 
                key={podcast.id}
                className="card-hover animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`podcast-${podcast.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex gap-4">
                    <div className="w-32 h-32 flex-shrink-0">
                      <img
                        src={podcast.image_url}
                        alt={podcast.title}
                        className="w-full h-full object-cover grayscale-hover"
                      />
                    </div>
                    <div className="flex-1 py-4 pr-4">
                      <h3 className="font-semibold text-lg mb-1">{podcast.title}</h3>
                      <p className="text-sm text-muted-foreground mb-2">by {podcast.host}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {podcast.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {podcast.episodes} episodes
                        </span>
                        {podcast.website && (
                          <a
                            href={podcast.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            Listen <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Content */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <h2 className="text-heading text-2xl mb-8">Video Content</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {videoContent.map((video, index) => (
              <Card 
                key={video.id}
                className="card-hover group animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`video-${video.id}`}
              >
                <CardContent className="p-0">
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover grayscale-hover"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-black ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Section */}
      <section className="bg-muted/30">
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <Headphones className="w-12 h-12 mx-auto mb-6 text-muted-foreground" />
            <h2 className="text-heading text-2xl mb-4">Have a Portland Tech Podcast?</h2>
            <p className="text-muted-foreground">
              If you run a podcast focused on Portland's technology scene, we'd love to feature it. 
              Reach out to us with your show details.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
